import {
  ChallengeEventType,
  ChallengeEventTypes,
  MatchMapping,
} from "../directus/collections";
import { isBuggedGame } from "../libs/gameAnalysis/bugs";
import {
  EnigmaMetrics,
  extractTimelines,
  isChallengeEvent,
  isChallengeHintEvent,
  isChallengeInitEvent,
  isChallengeRequestedSolutionEvent,
  parseAnswers,
} from "../libs/gameAnalysis/enigma";
import { analyzeHints } from "../libs/gameAnalysis/hints";
import { computeGamePathStatisticsFromEvents } from "../libs/gameAnalysis/paths";
import { groupEntries, mapEntries } from "../libs/gameAnalysis/utils";

import { GaripalliIDB } from "../extensions/idb";
import {
  isChallengeEndEvent,
  isChallengeErrorEvent,
  isChallengeSolutionEvent,
} from "../libs/gameAnalysis/enigma";
import { TimelineEntry } from "../libs/gameAnalysis/timeline";
import { average } from "../libs/utils";
import { ChallengeDetails, CityEscapeData } from "../types/cityEscape";
import { getGameLink } from "../utils/cityEscape";

export function aggregateEnigmaStats(
  id: string,
  stats: EnigmaMetrics[]
): EnigmaMetrics {
  const accumulator: EnigmaMetrics = {
    id,
    matchesMap: {},
    aggregators: {
      matches: [],
      solutionAsked: [],
      buggedGames: [],
      successes: [],
      drops: [],
      givenAnswers: [],
      matchesErrors: [],
      hints: {
        timelines: [],
        cleanTimelines: [],
        hintsPerMatch: [],
        matchesWithNoHintsProvided: [],
        periodsWithNoHints: [],
        matchesHints: [],
      },
      completionTimes: [],
      paths: [],
      timelines: [],
    },
  };

  return stats.reduce((acc, curr) => {
    return {
      id,
      matchesMap: { ...acc.matchesMap, ...curr.matchesMap },
      aggregators: {
        matches: acc.aggregators.matches.concat(curr.aggregators.matches),
        solutionAsked: acc.aggregators.solutionAsked.concat(
          curr.aggregators.solutionAsked
        ),
        buggedGames: acc.aggregators.buggedGames.concat(
          curr.aggregators.buggedGames || []
        ),
        successes: acc.aggregators.successes.concat(curr.aggregators.successes),
        drops: acc.aggregators.drops.concat(curr.aggregators.drops),
        givenAnswers: acc.aggregators.givenAnswers.concat(
          curr.aggregators.givenAnswers
        ),
        matchesErrors: acc.aggregators.matchesErrors.concat(
          curr.aggregators.matchesErrors
        ),
        hints: {
          timelines: acc.aggregators.hints.timelines.concat(
            curr.aggregators.hints.timelines
          ),
          cleanTimelines: acc.aggregators.hints.cleanTimelines.concat(
            curr.aggregators.hints.cleanTimelines
          ),
          hintsPerMatch: Object.entries(
            curr.aggregators.hints.hintsPerMatch
          ).reduce(
            (hpmAcc, [matchId, hints]) => {
              hpmAcc[Number(matchId)] = (hpmAcc[Number(matchId)] || 0) + hints;
              return hpmAcc;
            },
            { ...acc.aggregators.hints.hintsPerMatch }
          ),
          matchesWithNoHintsProvided:
            acc.aggregators.hints.matchesWithNoHintsProvided.concat(
              curr.aggregators.hints.matchesWithNoHintsProvided
            ),
          periodsWithNoHints: acc.aggregators.hints.periodsWithNoHints.concat(
            curr.aggregators.hints.periodsWithNoHints
          ),
          matchesHints: acc.aggregators.hints.matchesHints.concat(
            curr.aggregators.hints.matchesHints
          ),
        },
        completionTimes: acc.aggregators.completionTimes.concat(
          curr.aggregators.completionTimes
        ),
        paths: acc.aggregators.paths.concat(curr.aggregators.paths),
        timelines: acc.aggregators.timelines.concat(curr.aggregators.timelines),
      },
    };
  }, accumulator);
}

export function calcStats(enigma: ChallengeDetails, matchesMap: MatchMapping) {
  const effectivelyStartedEnigmas = Object.entries(matchesMap)
    .filter(([_, { events }]) => events.length > 0)
    .map(([key, _]) => key);

  const effectiveDrops = Object.entries(matchesMap)
    .filter(([_, { events }]) => !events.some(isChallengeEndEvent))
    .map(([matchId, events]) => matchId);

  // const allMatchesKeys = new Set([...Object.keys(matchesMap)]);

  // console.debug("All match keys:", allMatchesKeys);

  const buggedGames = Object.entries(matchesMap)
    .filter(([matchId, { match, events }]) =>
      isBuggedGame(match!, events, enigma.id)
    )
    .map(([matchId]) => matchId);

  // console.debug("Matches Map:", matchesMap);

  const matchesErrors = Object.entries(matchesMap).map(
    ([matchId, { match, events }]) => {
      const effectiveEnd = events.findIndex(isChallengeEndEvent);

      const errors = events
        .slice(0, effectiveEnd)
        .filter(isChallengeErrorEvent);

      return {
        matchId,
        value: errors.length,
        date: events[0]?.date_created,
      };
    }
  );

  const timelines = extractTimelines(matchesMap);

  const requestedAnswerTimes = [];
  const solvedTimes = [];
  const allTimes = [];

  for (const t of timelines) {
    if (t.length === 0) continue;

    const end = t.find((e) => isChallengeEndEvent(e.event));
    if (!end) continue;
    allTimes.push(end.timeFromStart);
    isChallengeSolutionEvent(end.event)
      ? solvedTimes.push(end)
      : requestedAnswerTimes.push(end);
  }

  return {
    id: enigma.id,
    matchesMap: matchesMap,
    aggregators: {
      matches: effectivelyStartedEnigmas,
      solutionAsked: requestedAnswerTimes as any[],
      buggedGames: buggedGames,
      successes: solvedTimes as any[],
      drops: effectiveDrops,
      givenAnswers: parseAnswers(
        enigma.data.managed_answers || [],
        Object.values(matchesMap).flatMap((m) => m.events)
      ),
      matchesErrors,
      hints: analyzeHints(matchesMap, enigma.id),
      completionTimes: allTimes as any[],
      paths: computeGamePathStatisticsFromEvents(enigma.id, matchesMap),
      timelines: timelines as any[],
    },
  };
}

export async function getUniquMatchesIds(
  db: GaripalliIDB,
  cityEscapeId?: string | null,
  enigmaId?: string
): Promise<string[]> {
  const store = db.objectStore("timelines");
  let entries = [];
  if (enigmaId) {
    const index = store.index("targetId", "readonly");
    entries = await index.getAll(enigmaId);
  } else if (cityEscapeId) {
    const index = store.index("byCityEscape", "readonly");
    entries = await index.getAll(cityEscapeId);
  } else {
    entries = await store.getAll();
  }
  const uniqueMatches = new Set(entries.map((entry) => entry.matchId));
  return Array.from(uniqueMatches);
}

export function aggregateUniqMatchesIds(
  entries: TimelineEntry[],
  matchFn: (entry: TimelineEntry) => boolean
): [Set<string>, Set<string>] {
  const allMatches = new Set<string>();
  const rateMatches = new Set<string>();
  for (const event of entries) {
    allMatches.add(event.matchId);
    if (matchFn(event)) {
      rateMatches.add(event.matchId);
    }
  }
  return [allMatches, rateMatches];
}

export function getTotalStarts(timelines: TimelineEntry[]): number {
  const grouped = groupEntries(timelines, (e) =>
    isChallengeEvent(e) ? `${e.matchId}@${e.targetId}` : null
  );
  console.debug("Total Starts:", grouped.length);
  return Object.keys(grouped).length;
}

export type MatchEnigmaIds = {
  matchId: string;
  targetId: string;
};

export type MatchEnigmaSummary = {
  matchId: string;
  targetId: string;
  startedAt: number | null;
  endedAt?: number | null;
  events: Record<ChallengeEventType, number>;
  gameLink?: string;
};

export type MatchEnigmaDetailedSummary = MatchEnigmaSummary & {
  teamName: string;
  challengeName: string;
};

export function createMatchEnigmaKey({
  matchId,
  targetId,
}: {
  matchId: string;
  targetId: string;
}): string {
  return `${matchId}::${targetId}`;
}

export function getMatchEnigmaIds(
  timelines: TimelineEntry[],
  filterFn: (entry: TimelineEntry) => boolean = () => true
): MatchEnigmaIds[] {
  return timelines.filter(filterFn).map((entry) => ({
    matchId: entry.matchId,
    targetId: entry.targetId,
  }));
}

export function toMatchEnigmaSummary(
  matchId: string,
  challengeId: string,
  entries: TimelineEntry[]
): MatchEnigmaSummary {
  return {
    matchId: matchId,
    targetId: challengeId,
    startedAt: entries.find((e) => e.challenge?.isStart)?.timestamp || null,
    events: {
      challenge_init: entries.filter((e) => isChallengeInitEvent(e)).length,
      challenge_hint: entries.filter((e) => isChallengeHintEvent(e)).length,
      challenge_error: entries.filter((e) => isChallengeErrorEvent(e)).length,
      challenge_solution: entries.filter((e) => isChallengeSolutionEvent(e))
        .length,
      challenge_requested_solution: entries.filter((e) =>
        isChallengeRequestedSolutionEvent(e)
      ).length,
    },
  };
}

export type MatchSummary = {
  id: string;
  team_name: string;
  order_type: string;
  order_id: string;
};

export type ChallengeSummary = {
  id: string;
  cityEscape: string;
  scene: string;
  indexInScene: number;
};

export function toChallengeSummary(
  {
    id,
    details,
    cityEscape,
  }: { id?: string; details?: ChallengeDetails; cityEscape?: CityEscapeData },
  opts?: { traceKey?: string; verbose?: boolean }
): ChallengeSummary {
  if (details) {
    return {
      id: id ?? details.id,
      cityEscape: details.cityEscape.title,
      scene: details.scene.name,
      indexInScene: details.meta.indexInScene,
    };
  } else if (cityEscape) {
    return {
      id: id ?? cityEscape.id,
      cityEscape: cityEscape.label,
      scene: "Unknown",
      indexInScene: 0,
    };
  }
}

export type MatchEnigmaDetailedSummaryProps = {
  match: MatchSummary;
  challenge: ChallengeSummary;
  entries: TimelineEntry[];
};

export function toMatchEnigmaDetailedSummary(
  match: {
    id: string;
    team_name: string;
    order_type: string;
    order_id: string;
  },
  challenge: {
    id: string;
    cityEscape: string;
    scene: string;
    indexInScene: number;
  },
  entries: TimelineEntry[]
): MatchEnigmaDetailedSummary {
  return {
    matchId: match.id,
    teamName: match.team_name,
    targetId: challenge.id,
    challengeName: `${challenge.cityEscape} / ${challenge.scene} / ${challenge.indexInScene}`,
    startedAt: entries.find((e) => e.challenge?.isStart)?.timestamp || null,
    gameLink: getGameLink(match),
    events: {
      challenge_init: entries.filter((e) => isChallengeInitEvent(e)).length,
      challenge_hint: entries.filter((e) => isChallengeHintEvent(e)).length,
      challenge_error: entries.filter((e) => isChallengeErrorEvent(e)).length,
      challenge_solution: entries.filter((e) => isChallengeSolutionEvent(e))
        .length,
      challenge_requested_solution: entries.filter((e) =>
        isChallengeRequestedSolutionEvent(e)
      ).length,
    },
  };
}

export function getMatchEnigmaSummary(
  timelines: TimelineEntry[],
  filterFn: (entry: TimelineEntry) => boolean = () => true
): MatchEnigmaSummary[] {
  const grouped = groupEntries(timelines, (e) => `${e.matchId}@${e.targetId}`);
  const accumulator: MatchEnigmaSummary[] = [];
  for (const [key, entries] of Object.entries(grouped)) {
    const [matchId, challengeId] = key.split("@");
    if (entries.some(filterFn)) {
      accumulator.push(toMatchEnigmaSummary(matchId, challengeId, entries));
    }
  }

  return accumulator;
}

export function calcAvgRate(
  timelines: TimelineEntry[],
  matchFn: (entry: TimelineEntry) => boolean,
  method: "per_challenge" | "per_match" = "per_match"
): number {
  const challengeEvents = timelines.filter((e) =>
    ChallengeEventTypes.includes(e.type as any)
  );
  const byChallenge = groupEntries(challengeEvents, (e) => e.targetId);
  if (method === "per_challenge") {
    const rates = Object.entries(byChallenge).map(([_, events]) => {
      const [allMatches, rateMatches] = aggregateUniqMatchesIds(
        events,
        matchFn
      );
      if (allMatches.size > 0) {
        return (rateMatches.size / allMatches.size) * 100;
      }
      return 0;
    });
    const avgRate =
      rates.reduce((acc, rate) => acc + rate, 0) / (rates.length || 1);
    return avgRate;
  } else {
    console.debug("Calculating average rate per match");
    const [all, successfull] = Object.values(byChallenge).reduce(
      ([allAcc, rateAcc], events) => {
        const [allMatches, rateMatches] = aggregateUniqMatchesIds(
          events,
          matchFn
        );
        return [allAcc + allMatches.size, rateAcc + rateMatches.size];
      },
      [0, 0]
    );

    if (all > 0) {
      return (successfull / all) * 100;
    }
    return 0;
  }
}

export function calcAvgTime(
  timelines: TimelineEntry[],
  key: (entry: TimelineEntry) => number = (e) => e.challenge?.timeFromStart ?? 0
): number {
  const byChallenge = mapEntries(
    timelines,
    (e) => (isChallengeEndEvent(e) ? `${e.matchId}@${e.targetId}` : null),
    {
      onConflict(existing, newEntry) {
        return existing.timeFromStart <= newEntry.timeFromStart
          ? existing
          : newEntry;
      },
    }
  );
  const values = Object.values(byChallenge).map((e) => key(e));
  return average(values) ?? 0;
}

export function getMatchesEnigmaTimelines(
  timelines: TimelineEntry[]
): TimelineEntry[][] {
  const grouped = groupEntries(timelines, (e) =>
    isChallengeEvent(e) ? `${e.matchId}@${e.targetId}` : null
  );
  return Object.values(grouped);
}

export function getHintsAggregateMap(timelines: TimelineEntry[]) {
  const grouped = groupEntries(timelines, (e) =>
    isChallengeHintEvent(e) ? `${e.matchId}@${e.targetId}` : null
  );
  const hintsMap: Record<
    string,
    Record<string, { matchId: string; hints: number; targetId: string }>
  > = {};

  for (const [key, events] of Object.entries(grouped)) {
    const [matchId, targetId] = key.split("@");
    hintsMap[targetId] = hintsMap[targetId] || {};
    const acc = { matchId, hints: 0, targetId };
    for (const event of events) {
      const hintNo = parseInt(event.value || "0") + 1;
      acc.hints = hintNo > acc.hints ? hintNo : acc.hints;
    }
    hintsMap[targetId][matchId] = acc;
  }

  const aggregates: Record<number, number> = {};
  for (const matchHints of Object.values(hintsMap)) {
    for (const { hints } of Object.values(matchHints)) {
      aggregates[hints] = (aggregates[hints] || 0) + 1;
    }
  }

  console.debug("Aggregates:", aggregates);

  return aggregates;
}

export function mapMatchHints(
  timelines: TimelineEntry[]
): Record<string, Record<string, number>> {
  return timelines.reduce((acc, event) => {
    acc[event.targetId] = acc[event.targetId] || {};
    acc[event.targetId][event.matchId] = event.challenge?.hintsClicks || 0;
    return acc;
  }, {} as Record<string, Record<string, number>>);
}

export function mapMatchErrors(
  timelines: TimelineEntry[]
): Record<string, Record<string, number>> {
  return timelines.reduce((acc, event) => {
    acc[event.targetId] = acc[event.targetId] || {};
    acc[event.targetId][event.matchId] = event.challenge?.errorsNo || 0;
    return acc;
    //
  }, {} as Record<string, Record<string, number>>);
}

export function calcMetricAvg(
  entries: TimelineEntry[],
  mapper: (entry: TimelineEntry[]) => Record<string, Record<string, number>>,
  method: "per_challenge" | "per_match" = "per_match"
): number {
  const filtered = entries.filter((e) =>
    ChallengeEventTypes.includes(e.type as any)
  );
  const mapped = mapper(filtered);
  let values: number[] = [];
  if (method === "per_match") {
    values = Object.entries(mapped).flatMap(([_, values]) => {
      return Object.values(values);
    });
  } else {
    values = Object.entries(mapped).map(([_, values]) => {
      const nums = Object.values(values);
      if (nums.length > 0) {
        return nums.reduce((acc, v) => acc + v, 0) / nums.length;
      }
      return 0;
    });
  }

  const avg = values.reduce((acc, v) => acc + v, 0) / (values.length || 1);
  return avg;
}

export function calcAvgErrors(
  timelines: TimelineEntry[],
  method: "per_challenge" | "per_match" = "per_match"
): number {
  return calcMetricAvg(timelines, mapMatchErrors, method);
}

export function calcAvgHints(timelines: TimelineEntry[]): number {
  return calcMetricAvg(timelines, mapMatchHints);
}
